import { useState, useEffect } from "react";
import axios from "axios";
import { brands } from "~src/api";
import { FullBrand } from "~src/types/brand";
import { logError } from "~src/utils/logger";

export type FullBrandLoadContext = {
  fullBrand: FullBrand | null;
  loading: boolean;
  error: string | null;
};

const useGetFullBrand = (
  brandId: number,
  days: number,
): FullBrandLoadContext => {
  const [loading, setLoading] = useState<boolean>(true);
  const [fullBrand, setFullBrand] = useState<FullBrand | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if brandId is invalid
    if (!brandId || brandId === 0) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const fullBrand = await brands.getFullBrand(brandId);

        // Only update state if component is still mounted
        if (isMounted) {
          setFullBrand(fullBrand);
        }
      } catch (error: any) {
        // Only handle errors if component is still mounted and it's not a cancellation
        if (isMounted && !axios.isCancel(error)) {
          logError(`Error fetching full brand: ${error.message}`);
          setError(error.message);
        }
        // For canceled requests, we silently ignore them
      } finally {
        // Always set loading to false if component is still mounted
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [brandId, days]);

  return { fullBrand, loading, error };
};

export default useGetFullBrand;
